import { setGameMode } from '@/lib/store/casino/actions';
import { CasinoGameMode } from '@/lib/store/casino/props';
import { AuthService } from '@/services/AuthService';
import { LoginByEmailSuccessOutputDto } from '@/services/dto/auth/auth-login-response.dto';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useRouter } from 'next/router';
import { usePathname } from './usePathname';
import { useModalNavigation, useSearchParams } from '@starsoft/common/hooks';
import { useSystemConfiguration } from './theme/useSystemConfiguration';
import { OriginId } from '@starsoft/common/models/origin/id';
import { CookiesKey, KYCStatus } from '@starsoft/common/enums';
import { AuthStep } from '@/enums/auth/authStep';
import { ModalsKey } from '@/enums/modalsKey';
import { Nullable } from '@starsoft/common/interfaces';
import { Kyc } from '@starsoft/common/models';
import { KYCQueryKeys, useKycMeByOriginId } from '@starsoft/common/api';
import baseAPI from '@/api';
import { UseKycMeQueryKeyProps } from '@starsoft/common/api/kyc/queries/useKycMeByOriginId/props';
import { setCookie } from 'cookies-next';

export function useLoginCallback() {
  const queryClient = useQueryClient();
  const { prefetch, replace } = useRouter();
  const pathname: string = usePathname();
  const dispatch = useDispatch();
  const search = useSearchParams();
  const { navigateToModal } = useModalNavigation();
  const { originId } = useSystemConfiguration();
  const { fetchKycMeByOriginId } = useKycMeByOriginId();

  const callback = useCallback(
    async (
      data: LoginByEmailSuccessOutputDto,
      path = pathname,
      isSessionByToken = false,
    ) => {
      try {
        AuthService.setAsAuthorized(data);

        const queryKey: UseKycMeQueryKeyProps = [
          KYCQueryKeys.Me,
          originId?.toString(),
        ];

        if (
          originId?.toString() === OriginId.Brazil?.toString() &&
          !isSessionByToken
        ) {
          await queryClient.prefetchQuery({
            queryKey: queryKey,
            queryFn: queryContext =>
              fetchKycMeByOriginId(queryContext, baseAPI),
          });
          const { data: kyc } =
            queryClient.getQueryState<Nullable<Kyc>>(queryKey);

          if (kyc?.status !== KYCStatus.Verified) {
            setCookie(CookiesKey.HasKyc, 'false', {
              sameSite: 'strict',
            });
            setCookie(CookiesKey.OriginId, originId?.toString(), {
              sameSite: 'strict',
            });
            search.set('step', AuthStep.Kyc);
            if (kyc?.tracking?.[0]?.checkpoint?.id) {
              search.set(
                'kyc-checkpoint-id',
                kyc?.tracking?.[0]?.checkpoint?.id?.toString(),
              );
            }
            navigateToModal(ModalsKey.Login, `?${search.toString()}`);
            return;
          }
        }

        setCookie(CookiesKey.HasKyc, 'true', {
          sameSite: 'strict',
        });
        dispatch(setGameMode(CasinoGameMode.Real));

        queryClient.removeQueries();
        queryClient.invalidateQueries();
        prefetch(path);
        replace(path?.includes('logout') ? '/' : path);
      } catch (error) {
        return;
      }
    },
    [
      dispatch,
      pathname,
      queryClient,
      prefetch,
      replace,
      search,
      navigateToModal,
      originId,
      fetchKycMeByOriginId,
    ],
  );

  return callback;
}
